<template>
  <div style="min-height: calc(100vh - 176px)" class="flex flex-wrap mt-4">
    <div
      class="relative py-2 px-2 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0"
      style="min-height: calc(-176px + 100vh);"
    >
      <div class="bg-white shadow-md p-4 rounded-lg flex flex-wrap gap-4 mb-4">
        <input
          type="text"
          v-model="searchQuery"
          @input="updateFilters"
          placeholder="Поиск по ученику, курсу, заказу..."
          class="border p-2 rounded w-full sm:w-1/3"
        />
        <select
          v-model="statusFilter"
          @change="updateFilters"
          class="border p-2 rounded w-full sm:w-1/4"
        >
          <option value="">Все статусы</option>
          <option value="pending">В ожидании</option>
          <option value="processing">Обработка</option>
          <option value="waiting">Ожидание</option>
          <option value="success">Успешно</option>
          <option value="failed">Ошибка</option>
          <option value="cancelled">Отменено</option>
        </select>
      </div>

      <div class="bg-white shadow-md rounded-lg overflow-hidden">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-200">
              <th class="p-3">#</th>
              <th class="p-3">Ученик</th>
              <th class="p-3">Курс</th>
              <th class="p-3">Сумма (KZT)</th>
              <th class="p-3">Статус</th>
              <th class="p-3">Создано</th>
              <th class="p-3">Обновлено</th>
              <th class="p-3">Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading" class="border-t">
              <td colspan="8" class="text-center p-4">Загрузка...</td>
            </tr>
            <tr v-else-if="filteredOrders.length === 0" class="border-t">
              <td colspan="8" class="text-center p-4">Нет заказов</td>
            </tr>
            <tr v-for="(order, index) in filteredOrders" :key="order.id" class="border-t hover:bg-gray-50">
              <td class="p-3">{{ index + 1 + (currentPage - 1) * perPage }}</td>
              <td class="p-3">
                <div>{{ order.student_name }}</div>
                <div class="text-xs text-gray-500">{{ order.student_login }}</div>
              </td>
              <td class="p-3">{{ order.course_title }}</td>
              <td class="p-3">{{ order.amount }}</td>
              <td class="p-3">
                <span
                  :class="{
                    'bg-yellow-100 text-yellow-800': order.status === 'pending',
                    'bg-blue-100 text-blue-800': order.status === 'processing',
                    'bg-gray-100 text-gray-800': order.status === 'waiting',
                    'bg-green-100 text-green-800': order.status === 'success',
                    'bg-red-100 text-red-800': ['failed', 'cancelled'].includes(order.status),
                  }"
                  class="px-3 py-1 rounded text-sm font-semibold"
                >
                  {{ statusLabels[order.status] || order.status }}
                </span>
              </td>
              <td class="p-3 text-sm">{{ formatDate(order.created_at) }}</td>
              <td class="p-3 text-sm">{{ formatDate(order.status_updated_at) }}</td>
              <td class="p-3">
                <button
                  @click="viewDetails(order)"
                  class="bg-blue-500 text-white px-3 py-1 rounded text-sm"
                >
                  Детали
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex justify-center mt-4">
        <button
          @click="changePage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 bg-gray-300 rounded mr-2 disabled:opacity-50"
        >
          ← Назад
        </button>
        <span class="px-4 py-2">Страница {{ currentPage }} из {{ totalPages }}</span>
        <button
          @click="changePage(currentPage + 1)"
          :disabled="currentPage >= totalPages"
          class="px-4 py-2 bg-gray-300 rounded ml-2 disabled:opacity-50"
        >
          Вперед →
        </button>
      </div>
    </div>

    <!-- Detail Modal -->
    <div
      v-if="selectedOrder"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-bold">Детали заказа</h2>
          <button @click="selectedOrder = null" class="text-gray-500 hover:text-gray-700">
            ✕
          </button>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <p class="font-semibold text-gray-700">Ученик:</p>
            <p>{{ selectedOrder.student_name }}</p>
            <p class="text-sm text-gray-600">{{ selectedOrder.student_login }}</p>
          </div>
          <div>
            <p class="font-semibold text-gray-700">Курс:</p>
            <p>{{ selectedOrder.course_title }}</p>
          </div>
          <div>
            <p class="font-semibold text-gray-700">Сумма:</p>
            <p>{{ selectedOrder.amount }} KZT</p>
          </div>
          <div>
            <p class="font-semibold text-gray-700">Статус:</p>
            <p>{{ statusLabels[selectedOrder.status] || selectedOrder.status }}</p>
          </div>
          <div>
            <p class="font-semibold text-gray-700">FreedomPay ID:</p>
            <p class="text-sm">{{ selectedOrder.freedom_pay_id }}</p>
          </div>
          <div>
            <p class="font-semibold text-gray-700">Заказ ID:</p>
            <p class="text-sm">{{ selectedOrder.order_id }}</p>
          </div>
          <div>
            <p class="font-semibold text-gray-700">Email:</p>
            <p class="text-sm">{{ selectedOrder.customer_email || '—' }}</p>
          </div>
          <div>
            <p class="font-semibold text-gray-700">Телефон:</p>
            <p class="text-sm">{{ selectedOrder.customer_phone || '—' }}</p>
          </div>
          <div>
            <p class="font-semibold text-gray-700">Создано:</p>
            <p class="text-sm">{{ formatDate(selectedOrder.created_at) }}</p>
          </div>
          <div>
            <p class="font-semibold text-gray-700">Обновлено:</p>
            <p class="text-sm">{{ formatDate(selectedOrder.status_updated_at) }}</p>
          </div>
          <div v-if="selectedOrder.paid_at">
            <p class="font-semibold text-gray-700">Оплачено:</p>
            <p class="text-sm">{{ formatDate(selectedOrder.paid_at) }}</p>
          </div>
          <div v-if="selectedOrder.payment_method">
            <p class="font-semibold text-gray-700">Метод платежа:</p>
            <p class="text-sm">{{ selectedOrder.payment_method }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { BASE_URL, HEADERS_TOKEN_JSON, DEFAULT_PER_PAGE } from '@/const/uri';

export default {
  data() {
    return {
      orders: [],
      loading: false,
      selectedOrder: null,
      searchQuery: '',
      statusFilter: '',
      currentPage: 1,
      perPage: DEFAULT_PER_PAGE,
      totalPages: 1,
      statusLabels: {
        pending: 'В ожидании',
        processing: 'Обработка',
        waiting: 'Ожидание',
        success: 'Успешно',
        failed: 'Ошибка',
        cancelled: 'Отменено',
      },
    };
  },
  computed: {
    filteredOrders() {
      return this.orders.filter((order) => {
        const matchesSearch =
          (order.student_name || '').toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          (order.course_title || '').toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          (order.order_id || '').toLowerCase().includes(this.searchQuery.toLowerCase());

        const matchesStatus = !this.statusFilter || order.status === this.statusFilter;

        return matchesSearch && matchesStatus;
      });
    },
  },
  watch: {
    '$route.query': {
      immediate: true,
      handler() {
        this.fetchOrders();
      },
    },
  },
  mounted() {
    this.fetchOrders();
  },
  methods: {
    async fetchOrders() {
      const query = this.$route.query;

      this.searchQuery = query.search || '';
      this.statusFilter = query.status || '';
      this.currentPage = Number(query.page) || 1;

      this.loading = true;
      try {
        const response = await axios.get(
          `${BASE_URL}/api/admin/payment/orders/`,
          {
            params: {
              search: this.searchQuery || undefined,
              status: this.statusFilter || undefined,
              p: this.currentPage,
              page_size: this.perPage,
            },
            headers: HEADERS_TOKEN_JSON(),
          }
        );

        const data = response.data.data;

        // Handle paginated response from admin endpoint
        if (data && data.results && Array.isArray(data.results)) {
          this.orders = data.results;
          this.totalPages = Math.ceil(data.count / this.perPage);
        } else if (Array.isArray(data)) {
          this.orders = data;
          this.totalPages = 1;
        } else {
          this.orders = [];
          this.totalPages = 1;
        }
      } catch (e) {
        console.error('Failed to load orders:', e);
        alert('Ошибка при загрузке заказов');
      } finally {
        this.loading = false;
      }
    },
    updateFilters() {
      this.$router.push({
        query: {
          search: this.searchQuery || undefined,
          status: this.statusFilter || undefined,
          page: 1,
        },
      });
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.$router.push({ query: { ...this.$route.query, page } });
      }
    },
    viewDetails(order) {
      this.selectedOrder = order;
    },
    formatDate(dateString) {
      if (!dateString) return '—';
      const date = new Date(dateString);
      return date.toLocaleString('ru-RU');
    },
  },
};
</script>
